import { twMerge } from "tailwind-merge";
import { isFailed, isIgnored, TriggerStep } from "~/lib/pipeline";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import Emojify from "~/components/shared/Emojify";
import { Label } from "~/components/build/Show/pages/steps/list/components/TreeNode/Label";
import { StepLink } from "~/components/build/Show/pages/steps/list/components/TreeNode/StepLink";
import { resolveStepUrl } from "~/components/build/Show/lib/urlForView";
import {
  TreeNode,
  TreeNodeType,
} from "~/components/build/Show/pages/steps/list/components/TreeNode";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";

interface Props {
  step: TriggerStep;
  depth?: number;
}

export function TriggerStepItem({ step, depth = 0 }: Props) {
  const { missingDependencies } = useStepDependencies(step.uuid);

  const params = useQueryParams();
  const label = step.label || "Trigger build";
  const isStepIgnored = isIgnored(step);

  return (
    <StepLink
      to={isStepIgnored ? resolveStepUrl(step.uuid, { open: false }) : resolveStepUrl(step.uuid)}
      type={step.type}
      state={step.state}
      active={step.uuid === params.stepUuid}
      failed={isFailed(step)}
      depth={depth}
      disabled={isStepIgnored}
      title={isStepIgnored ? "Skipped steps cannot be viewed" : undefined}
    >
      <Label
        duration={null}
        status={
          <StateIcon
            {...step}
            missingDependencies={missingDependencies}
            className={twMerge("-mr-0.5 w-4", textColorForStep({ ...step, missingDependencies }))}
          />
        }
      >
        <Emojify text={label} />
        <span className="text-charcoal-300 font-mono text-xs font-normal">
          {step.triggerBranch && step.triggerCommit
            ? `${step.triggerBranch}/${step.triggerCommit} on ${step.triggerPipelineSlug}`
            : step.triggerPipelineSlug}
        </span>
      </Label>
    </StepLink>
  );
}

export const createTriggerStepNode = (step: TriggerStep): TreeNode => ({
  id: step.uuid,
  type: TreeNodeType.StepTrigger,
  render: (props) => <TriggerStepItem step={step} {...props} />,
});
